import { C } from "@bd/content";
import { BALANCE } from "./balance";
import { priceIndex, yearOf } from "./calendar";
import { addDecision, type DecisionHandler } from "./company";
import { rivalActive } from "./market";
import { addMod } from "./mods";
import type { Company, GameState, RivalState } from "./state";
import { clamp, log, money, rng } from "./util";

/**
 * Rivals are studios too: how many people they have and what kind of building they're in, worked out from how well
 * they're doing (no extra save data).
 */
export function rivalStudio(rs: RivalState, week: number): { staff: number; level: "garage" | "office" | "studio" | "hq" } {
  const rc = C.rival[rs.id];
  const years = Math.max(0, yearOf(week) - Math.max(1965, rc?.from ?? 1965, rs.foundedWeek !== undefined ? yearOf(rs.foundedWeek) : 1965));
  const growth = Math.min(1, years / 25);
  const staff = Math.max(2, Math.round((3 + (rs.reputation / 100) * 45 * growth + Math.min(10, rs.released / 15)) * (rc?.acquirer ? 1.6 : 1)));
  const level = staff <= 4 ? "garage" : staff <= 12 ? "office" : staff <= 30 ? "studio" : "hq";
  return { staff, level };
}

/** Rivals don't just release games: now and then one makes a move against you. */
export function rivalMovesWeek(state: GameState, co: Company): void {
  if (co.bankrupt || yearOf(state.week) < 1968 || state.week % 4 !== 1) return;
  const r = rng(state, `moves:${co.id}`);
  const open = state.rivals.filter((rs) => rivalActive(rs, C.rival[rs.id], state.week));
  if (!open.length) return;
  // one move at a time, and not too often
  if (co.pending.some((d) => d.kind.startsWith("rival_")) || state.week - (co.lastRivalMove ?? -999) < BALANCE.rivalMoveGapWeeks) return;

  // a price war on the audience of your latest hit
  const hit = co.products.filter((p) => p.launched && p.score >= 8 && state.week - p.releaseWeek < 12 && p.kind === "base").at(-1);
  if (hit && r.chance(0.35)) {
    const rs = r.pick(open.filter((x) => C.rival[x.id].audiences.includes(hit.design.audience)).concat(open).slice(0, 4));
    const aud = C.audience[hit.design.audience].name;
    co.lastRivalMove = state.week;
    addDecision(state, co, {
      kind: "rival_pricewar", weeks: 3, defaultOption: "hold",
      title: `${C.rival[rs.id].name} starts a price war`,
      text: `${hit.design.name} is a hit, so ${C.rival[rs.id].name} has cut prices on everything for ${aud} players. Shops are asking if you'll match.`,
      options: [
        { id: "match", label: "Match their prices", detail: "Your sales hold up, but every sale earns 8% less for 12 weeks" },
        { id: "hold", label: "Hold your prices", detail: `Your ${aud} games sell 15% less for 12 weeks` },
      ],
      data: { rival: rs.id, audience: hit.design.audience },
    });
    return;
  }

  // a knock-off of one of your games just came out
  const mine = new Set(co.products.map((p) => p.marketId).filter(Boolean));
  const copy = state.market.games.filter((g) => g.copyOf && mine.has(g.copyOf) && state.week - g.week <= 4 && C.rival[g.publisher]).at(-1);
  if (copy && r.chance(0.6)) {
    const orig = co.products.find((p) => p.marketId === copy.copyOf)!;
    const fee = Math.round(BALANCE.lawyerFee * priceIndex(state.week));
    co.lastRivalMove = state.week;
    addDecision(state, co, {
      kind: "rival_knockoff", weeks: 3, defaultOption: "ignore",
      title: `${C.rival[copy.publisher].name} copies ${orig.design.name}`,
      text: `${copy.name} looks a lot like ${orig.design.name}: same idea, same audience, a cheaper box.`,
      options: [
        { id: "lawyers", label: `Send the lawyers (${money(fee)})`, detail: "About an even chance they pull it, and your original gets the shelf back" },
        { id: "ignore", label: "Let it go", detail: "Some players go looking for the original: a few more fans" },
      ],
      data: { rival: copy.publisher, productId: orig.id, fee },
    });
    return;
  }

  // a rival bids against you for a licence on offer
  const offer = (co.licenceOffers ?? []).find((o) => !o.contested && o.expires - state.week > 6 && o.expires - state.week < 20);
  if (offer && r.chance(0.4)) {
    const L = C.licence[offer.id];
    const rs = r.pick(open);
    offer.contested = true;
    co.lastRivalMove = state.week;
    addDecision(state, co, {
      kind: "rival_licence", weeks: 3, defaultOption: "drop",
      title: `${C.rival[rs.id].name} wants ${L.name} too`,
      text: `The owners of ${L.name} have a second offer, from ${C.rival[rs.id].name}. Raise yours, or let it go?`,
      options: [
        { id: "raise", label: `Raise the bid to ${money(L.fee * BALANCE.licenceRaise * priceIndex(state.week))}`, detail: "The licence stays yours to take" },
        { id: "drop", label: "Let it go", detail: `${C.rival[rs.id].name} makes the game instead` },
      ],
      data: { rival: rs.id, licence: offer.id },
    });
  }
}

/** The answers to rival moves (looked up when a decision resolves; see company.ts). */
export const RIVAL_DECISIONS: Record<string, DecisionHandler> = {};

RIVAL_DECISIONS.rival_pricewar = (state, co, d, option) => {
  const name = C.rival[d.data!.rival].name;
  if (option === "match") {
    addMod(state, co, { kind: "margin", value: 0.92, target: "all", weeks: 12, label: `Price war with ${name}` });
    log(state, "rival_move", `You match ${name}'s prices: sales hold, margins dip for 12 weeks.`, co.id);
  } else {
    addMod(state, co, { kind: "audience", value: 0.85, target: d.data!.audience, weeks: 12, label: `${name}'s price cuts` });
    log(state, "rival_move", `You hold your prices; ${name}'s cheaper boxes take some of your sales for 12 weeks.`, co.id);
  }
};

RIVAL_DECISIONS.rival_knockoff = (state, co, d, option) => {
  const rs = state.rivals.find((x) => x.id === d.data!.rival);
  const name = C.rival[d.data!.rival].name;
  const p = co.products.find((x) => x.id === d.data!.productId);
  if (option === "lawyers") {
    co.cash -= d.data!.fee;
    if (rng(state, `lawyers:${d.id}`).chance(0.5)) {
      if (rs) rs.reputation = clamp(rs.reputation - 5, 0, 100);
      if (p) addMod(state, co, { kind: "sales", value: 1.12, target: `product:${p.id}`, weeks: 26, label: "The knock-off is pulled" });
      log(state, "rival_move", `${name} pulls its knock-off. ${p?.design.name ?? "Your game"} gets the shelf back.`, co.id);
    } else log(state, "rival_move", `${name}'s lawyers win this one: the knock-off stays on the shelf.`, co.id);
  } else {
    co.fans += Math.round(500 + co.fans * 0.01);
    log(state, "rival_move", `You let ${name}'s knock-off go. A few players go looking for the original.`, co.id);
  }
};

RIVAL_DECISIONS.rival_licence = (state, co, d, option) => {
  const offer = (co.licenceOffers ?? []).find((o) => o.id === d.data!.licence);
  const name = C.rival[d.data!.rival].name;
  if (!offer) return;
  if (option === "raise") {
    offer.feeMult = BALANCE.licenceRaise;
    log(state, "rival_move", `You outbid ${name} for ${C.licence[offer.id].name}.`, co.id);
  } else {
    co.licenceOffers = co.licenceOffers.filter((o) => o !== offer);
    log(state, "rival_move", `${name} takes the ${C.licence[offer.id].name} licence.`, co.id);
  }
};

